import { configureStore } from "@reduxjs/toolkit";
import {
	FLUSH,
	PAUSE,
	PERSIST,
	type PersistedState,
	PURGE,
	persistReducer,
	persistStore,
	REGISTER,
	REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import billingSlice from "./slices/billingSlice";
import clapSlice from "./slices/clapSlice";
import customizerSlice from "./slices/customizerSlice";
import dashboardSlice from "./slices/dashboardSlice";
import geocodeFiltersSlice from "./slices/geocodeFiltersSlice";
import globalTrackingSlice from "./slices/globalTrackingSlice";
import loadCalculatorSlice from "./slices/loadCalculatorSlice";
import notificationsSlice from "./slices/notificationsSlice";
import pageSizeSlice from "./slices/pageSizeSlice";
import pinnedSearchSlice from "./slices/pinnedSearchSlice";
import portCongestionSlice from "./slices/portCongestionSlice";
import quotesSlice from "./slices/quotesSlice";
import shipmentsSlice from "./slices/shipmentsSlice";
import tableViewSlice from "./slices/tableViewSlice";
import topPerformersSlice from "./slices/topPerformersSlice";
import trackingViewSlice from "./slices/trackingViewSlice";
import unitsSlice from "./slices/unitsSlice";

const customizerPersistConfig = {
	key: "customizer",
	storage,
	// 5: the theme picker was removed and the palette frozen. Without this bump
	// everyone already using the app keeps a persisted VIOLET_THEME that nothing
	// can change any more.
	//
	// 6: the border-radius slider and the direction toggle went the same way.
	// The slice no longer declares them, but a browser that stored them would
	// keep merging them back into state on every load - keys nothing reads, and
	// a radius that no longer means anything.
	version: 6,
	migrate: (state: PersistedState): Promise<PersistedState> => {
		if (!state) return Promise.resolve(state);
		const { activeDir, isBorderRadius, ...rest } = state as PersistedState &
			Record<string, unknown>;
		return Promise.resolve(rest as PersistedState);
	},
	// Only genuinely transient state stays out: whether the mobile drawer is
	// open and whether the cursor is over the sidebar. `isCollapse` is a
	// preference the user sets in settings/appearance, so it persists.
	blacklist: ["isMobileSidebar", "isSidebarHover"],
};

const shipmentsPersistConfig = {
	key: "shipments",
	storage,
	/*
		Bumped when the default filter became "active".

		The slice's `initialState` only reaches somebody with nothing stored, and
		this filter has been persisted since it shipped - so without a version
		bump the new default would have applied to new browsers only, and everyone
		already using the app would have kept landing on "all" wondering what
		changed. One reset, then the choice is theirs again.
	*/
	version: 1,
	migrate: (state: PersistedState): Promise<PersistedState> =>
		Promise.resolve({
			activeButton: "active",
			_persist: state?._persist ?? { version: 1, rehydrated: false },
		}),
};

const billingPersistConfig = {
	key: "billing",
	storage,
};

const quotesPersistConfig = {
	key: "quotes",
	storage,
};

const dashboardPersistConfig = {
	key: "dashboard",
	storage,
};

const topPerformersPersistConfig = {
	key: "topPerformers",
	storage,
};

const clapPersistConfig = {
	key: "clap",
	storage,
};

const unitsPersistConfig = {
	key: "units",
	storage,
};

const notificationsPersistConfig = {
	key: "notifications",
	storage,
	version: 3,
	migrate: (state: PersistedState): Promise<PersistedState> => {
		// Reset state when version changes to clear old data structure
		return Promise.resolve({
			notifications: [],
			unreadCount: 0,
			loading: false,
			lastFetched: null,
			selectedNotification: null,
			filter: "all",
			searchQuery: "",
			_persist: state?._persist ?? { version: 3, rehydrated: false },
		});
	},
};

const persistedUnitsReducer = persistReducer(unitsPersistConfig, unitsSlice);

const persistedCustomizerReducer = persistReducer(
	customizerPersistConfig,
	customizerSlice,
);
const persistedShipmentsReducer = persistReducer(
	shipmentsPersistConfig,
	shipmentsSlice,
);
const persistedBillingReducer = persistReducer(
	billingPersistConfig,
	billingSlice,
);
const persistedQuotesReducer = persistReducer(quotesPersistConfig, quotesSlice);
const persistedDashboardReducer = persistReducer(
	dashboardPersistConfig,
	dashboardSlice,
);
const persistedTopPerformersReducer = persistReducer(
	topPerformersPersistConfig,
	topPerformersSlice,
);
const persistedClapReducer = persistReducer(clapPersistConfig, clapSlice);
const persistedNotificationsReducer = persistReducer(
	notificationsPersistConfig,
	notificationsSlice,
);

const pinnedSearchPersistConfig = {
	key: "pinnedSearch",
	storage,
};
const persistedPinnedSearchReducer = persistReducer(
	pinnedSearchPersistConfig,
	pinnedSearchSlice,
);

// Persist only origin/destination ports.
const geocodeFiltersPersistConfig = {
	key: "geocodeFilters",
	storage,
	whitelist: ["codeFrom", "codeTo"],
};
const persistedGeocodeFiltersReducer = persistReducer(
	geocodeFiltersPersistConfig,
	geocodeFiltersSlice,
);

// The chosen severity, and nothing else the page holds.
const portCongestionPersistConfig = {
	key: "portCongestion",
	storage,
};
const persistedPortCongestionReducer = persistReducer(
	portCongestionPersistConfig,
	portCongestionSlice,
);

// Whether the map tours the fleet, and which half of the list is open.
const globalTrackingPersistConfig = {
	key: "globalTracking",
	storage,
};
const persistedGlobalTrackingReducer = persistReducer(
	globalTrackingPersistConfig,
	globalTrackingSlice,
);

// Rows or cards, for every table at once.
const tableViewPersistConfig = {
	key: "tableView",
	storage,
};
const persistedTableViewReducer = persistReducer(
	tableViewPersistConfig,
	tableViewSlice,
);

// List or table, for the tracking Containers tab.
const trackingViewPersistConfig = {
	key: "trackingView",
	storage,
};
const persistedTrackingViewReducer = persistReducer(
	trackingViewPersistConfig,
	trackingViewSlice,
);

// Rows per page, per list table.
const pageSizePersistConfig = {
	key: "pageSize",
	storage,
};
const persistedPageSizeReducer = persistReducer(
	pageSizePersistConfig,
	pageSizeSlice,
);

export const store = configureStore({
	reducer: {
		customizer: persistedCustomizerReducer,
		geocodeFilters: persistedGeocodeFiltersReducer,
		shipments: persistedShipmentsReducer,
		billing: persistedBillingReducer,
		quotes: persistedQuotesReducer,
		dashboard: persistedDashboardReducer,
		topPerformers: persistedTopPerformersReducer,
		clap: persistedClapReducer,
		units: persistedUnitsReducer,
		loadCalculator: loadCalculatorSlice,
		notifications: persistedNotificationsReducer,
		pinnedSearch: persistedPinnedSearchReducer,
		portCongestion: persistedPortCongestionReducer,
		globalTracking: persistedGlobalTrackingReducer,
		tableView: persistedTableViewReducer,
		trackingView: persistedTrackingViewReducer,
		pageSize: persistedPageSizeReducer,
	},
	devTools: process.env.NODE_ENV !== "production",
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
